import React, { useCallback, useEffect, useState } from 'react';
import { message, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  standardCalcService,
  StandardCalculation,
  CreateStandardCalculationPayload,
} from '../../ConfigurationsView/CalcProfile/StandardCalcService';
import { MODAL_STYLES, MODAL_TITLE_STYLE } from '../../../styles/modalStyles';
import styles from './StandardCalculations.module.css';

// ── Category badge ─────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  Tax:          { bg: 'rgba(59, 130, 246, 0.15)',  color: '#60a5fa' },
  Allocation:   { bg: 'rgba(234, 88, 12, 0.15)',   color: '#fb923c' },
  Capital:      { bg: 'rgba(124, 58, 237, 0.15)',  color: '#a78bfa' },
  Depreciation: { bg: 'rgba(20, 184, 166, 0.15)',  color: '#2dd4bf' },
  Output:       { bg: 'rgba(34, 197, 94, 0.15)',   color: '#22c55e' },
  Event:        { bg: 'rgba(156, 163, 175, 0.15)', color: '#9ca3af' },
};

function CategoryBadge({ category }: { category: string }) {
  const c = CATEGORY_COLORS[category] ?? { bg: 'rgba(156, 163, 175, 0.15)', color: '#9ca3af' };
  return (
    <span style={{ background: c.bg, color: c.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
      {category || '—'}
    </span>
  );
}

function TypeBadge({ periodType }: { periodType: string }) {
  const isEvent = periodType === 'Event';
  return (
    <span style={{
      background: isEvent ? 'rgba(234, 88, 12, 0.15)' : 'rgba(59, 130, 246, 0.15)',
      color:      isEvent ? '#fb923c'                  : '#60a5fa',
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    }}>
      {isEvent ? 'Event' : 'Period'}
    </span>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span style={{
      background: isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(156, 163, 175, 0.15)',
      color:      isActive ? '#22c55e'                  : '#9ca3af',
      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    }}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

// ── Dependency resolver ────────────────────────────────────────────────────────

function resolveDeps(deps: string | null | undefined, allCalcs: StandardCalculation[]): string {
  if (!deps) return '—';
  const codes = deps.split(',').map(s => s.trim()).filter(Boolean);
  if (codes.length === 0) return '—';
  const codeMap = new Map(allCalcs.map(c => [c.code, c.name]));
  return codes.map(code => codeMap.get(code) ?? code).join(', ');
}

// ── Section table ──────────────────────────────────────────────────────────────

function SectionTable({ title, calcs, allCalcs, search }: {
  title: string;
  calcs: StandardCalculation[];
  allCalcs: StandardCalculation[];
  search: string;
}) {
  const filtered = calcs.filter(c =>
    !search ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.category ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>{title}</div>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Calculation Name</th>
            <th>Category</th>
            <th>Type</th>
            <th>Dependencies</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={5} className={styles.emptyCell}>
                {search ? `No results for "${search}"` : `No ${title.toLowerCase()} found.`}
              </td>
            </tr>
          ) : filtered.map(c => (
            <tr key={c.id} className={styles.tableRow}>
              <td><div className={styles.cellName}>{c.name}</div></td>
              <td><CategoryBadge category={c.category ?? ''} /></td>
              <td><TypeBadge periodType={c.periodType} /></td>
              <td style={{ fontSize: 13, color: 'var(--ce-text-muted)', maxWidth: 300 }}>
                {resolveDeps(c.dependencies, allCalcs)}
              </td>
              <td><StatusBadge isActive={c.isActive} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.tableFooter}>
        {filtered.length} calculation{filtered.length !== 1 ? 's' : ''}
        {search ? ` matching "${search}"` : ''}
      </div>
    </div>
  );
}

// ── Create modal ───────────────────────────────────────────────────────────────

const CATEGORIES = ['Tax', 'Allocation', 'Capital', 'Depreciation', 'Output', 'Event'] as const;

interface ModalForm {
  name: string;
  category: string;
  periodType: string;
  depCodes: string[];
}

const LABEL: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
  fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const INPUT: React.CSSProperties = {
  height: 36, padding: '0 10px',
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 6, color: '#fff',
  fontSize: 13, fontFamily: 'Inter, sans-serif', outline: 'none',
};

const BTN_CANCEL: React.CSSProperties = {
  height: 34, padding: '0 14px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 6, color: 'rgba(255,255,255,0.7)',
  fontSize: 13, fontFamily: 'Inter, sans-serif', cursor: 'pointer',
};

const BTN_SAVE: React.CSSProperties = {
  height: 34, padding: '0 16px',
  background: 'var(--ce-accent)', border: 'none',
  borderRadius: 6, color: '#fff',
  fontSize: 13, fontWeight: 600, fontFamily: 'Inter, sans-serif', cursor: 'pointer',
};

function CalcModal({ open, saving, allCalcs, onCancel, onSave }: {
  open: boolean;
  saving: boolean;
  allCalcs: StandardCalculation[];
  onCancel: () => void;
  onSave: (form: ModalForm) => void;
}) {
  const [form, setForm] = useState<ModalForm>({
    name: '', category: 'Tax', periodType: 'Operational', depCodes: [],
  });

  useEffect(() => {
    if (open) setForm({ name: '', category: 'Tax', periodType: 'Operational', depCodes: [] });
  }, [open]);

  const set = (k: keyof Omit<ModalForm, 'depCodes'>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }));

  const toggleDep = (code: string) =>
    setForm(prev => ({
      ...prev,
      depCodes: prev.depCodes.includes(code)
        ? prev.depCodes.filter(c => c !== code)
        : [...prev.depCodes, code],
    }));

  const activeDeps = allCalcs.filter(c => c.isActive);

  return (
    <Modal open={open} onCancel={onCancel} footer={null} styles={MODAL_STYLES} width={480}>
      <h3 style={MODAL_TITLE_STYLE}>Create Standard Calculation</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>

        <label style={LABEL}>
          Name
          <input style={INPUT} value={form.name} onChange={set('name')} placeholder="e.g. Partnership Income Allocation" />
        </label>

        <label style={LABEL}>
          Category
          <select style={INPUT} value={form.category} onChange={set('category')}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label style={LABEL}>
          Type
          <select style={INPUT} value={form.periodType} onChange={set('periodType')}>
            <option value="Operational">Period</option>
            <option value="Event">Event</option>
          </select>
        </label>

        {activeDeps.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Dependencies
            </span>
            <div style={{
              maxHeight: 180, overflowY: 'auto',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6, padding: '8px 12px',
              display: 'flex', flexDirection: 'column', gap: 7,
            }}>
              {activeDeps.map(c => (
                <label key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#fff' }}>
                  <input
                    type="checkbox"
                    checked={form.depCodes.includes(c.code)}
                    onChange={() => toggleDep(c.code)}
                    style={{ accentColor: 'var(--ce-accent)' }}
                  />
                  <span style={{ flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{c.code}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button style={BTN_CANCEL} onClick={onCancel} disabled={saving}>Cancel</button>
          <button
            style={{ ...BTN_SAVE, opacity: (!form.name.trim() || saving) ? 0.5 : 1, cursor: (!form.name.trim() || saving) ? 'not-allowed' : 'pointer' }}
            onClick={() => form.name.trim() && !saving && onSave(form)}
            disabled={!form.name.trim() || saving}
          >
            {saving ? 'Saving…' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export const StandardCalculations: React.FC = () => {
  const [calcs, setCalcs]       = useState<StandardCalculation[]>([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [modalOpen, setModal]   = useState(false);
  const [saving, setSaving]     = useState(false);

  const fetchCalcs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await standardCalcService.getAll();
      setCalcs(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 404) message.error('Failed to load standard calculations');
      setCalcs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCalcs(); }, [fetchCalcs]);

  const periodCalcs = calcs.filter(c => c.periodType !== 'Event');
  const eventCalcs  = calcs.filter(c => c.periodType === 'Event');

  const handleCreate = async (form: ModalForm) => {
    setSaving(true);
    try {
      // auto-generate next code from highest existing SC-NNN
      const existing = calcs.map(c => {
        const m = c.code?.match(/^SC-(\d+)$/i);
        return m ? parseInt(m[1], 10) : 0;
      });
      const next = (Math.max(0, ...existing) + 1).toString().padStart(3, '0');

      const payload: CreateStandardCalculationPayload = {
        code:         `SC-${next}`,
        name:         form.name.trim(),
        description:  '',
        periodType:   form.periodType,
        category:     form.category,
        dependencies: form.depCodes.join(','),
        sortOrder:    (calcs.length + 1) * 10,
      };

      await standardCalcService.create(payload);
      message.success('Standard calculation created');
      setModal(false);
      await fetchCalcs();
    } catch {
      message.error('Failed to create standard calculation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrap}>

      {/* ── Header ── */}
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Standard Calculations</h1>
          <p className={styles.pageSub}>
            The pre-built tax formula library — defined once at the platform level, available to every partnership.
          </p>
        </div>
        <div className={styles.headActions}>
          <button className={styles.btnOutline} onClick={() => message.info('Import — coming soon')}>
            Import
          </button>
          <button className={styles.btnPrimary} onClick={() => setModal(true)}>
            <PlusOutlined /> Create Calculation
          </button>
        </div>
      </div>

      {/* ── Search ── */}
      <div className={styles.filterRow}>
        <div className={styles.searchWrap}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            className={styles.searchInput}
            placeholder="Search calculations…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Content ── */}
      <div className={styles.tableWrap}>
        {loading ? (
          <div className={styles.loadingState}>
            <div style={{ color: 'var(--ce-text-muted)', fontSize: 14 }}>Loading…</div>
          </div>
        ) : (
          <>
            <SectionTable
              title="Period Calculations"
              calcs={periodCalcs}
              allCalcs={calcs}
              search={search}
            />
            <div className={styles.divider} />
            <SectionTable
              title="Event Calculations"
              calcs={eventCalcs}
              allCalcs={calcs}
              search={search}
            />
          </>
        )}
      </div>

      <CalcModal
        open={modalOpen}
        saving={saving}
        allCalcs={calcs}
        onCancel={() => setModal(false)}
        onSave={handleCreate}
      />
    </div>
  );
};
